
import Vue, { PropType } from 'vue'
import api from '@root/src/api/api'
import Opz2Icon from './Opz2Icon.vue'
import {
    DASH,
    FUNCTION_STATUS,
    Report,
    Row,
    STATUS_LABEL,
    adminLabel,
    cnt,
    has,
    list,
    longDate,
    money,
    shortDate,
    yclientsUrl,
} from './model'

/**
 * Карточка клиента — выход client_card функции (filter.client_id) как есть:
 * visits_cnt, paid_sum, records[]. Нет выхода или поля — «—».
 */
export default Vue.extend({
    name: 'Opz2ClientDrawer',
    components: { Opz2Icon },
    props: {
        projectId: { type: String, required: true },
        clientId: { type: String as PropType<string | null>, default: null },
        report: { type: Object as PropType<Report>, required: true },
        /** параметры запроса отчёта: период, филиал, администратор */
        request: { type: Object as PropType<Record<string, any>>, required: true },
        projectName: { type: String, default: '' },
    },
    data: () => ({
        STATUS_LABEL,
        DASH,
        loaded: false,
        card: {} as Row,
        names: null as any,
        loading: false,
        error: false,
        visible: false,
        opener: null as HTMLElement | null,
        closeTimer: 0 as any,
    }),
    computed: {
        dictionaries(): Report {
            return this.names ? ({ ...this.report, ...this.names } as Report) : this.report
        },
        records(): Row[] {
            return Array.isArray(this.card.records) ? this.card.records : []
        },
        client(): { name: string | null; hrefInfo: any } | undefined {
            return this.dictionaries.clients[this.clientId ?? '']
        },
        name(): string {
            return this.client?.name || this.clientId || ''
        },
        initials(): string {
            return this.name
                .split(' ')
                .filter(Boolean)
                .map(part => part[0])
                .join('')
                .slice(0, 2)
        },
        ycUrl(): string | null {
            return yclientsUrl(this.client?.hrefInfo)
        },
        phone(): string | null {
            const info = this.client?.hrefInfo
            const items = Array.isArray(info) ? info : []
            return items.find((item: any) => item?.phone)?.phone ?? null
        },
    },
    watch: {
        clientId(id: string | null) {
            if (id) this.open()
        },
    },
    beforeDestroy() {
        clearTimeout(this.closeTimer)
        document.body.style.overflow = ''
    },
    methods: {
        cnt,
        money,
        list,
        dateText(day: unknown, long = false) {
            return has(day) ? (long ? longDate(String(day)) : shortDate(String(day))) : DASH
        },
        statusOf(record: Row) {
            return has(record.status) ? FUNCTION_STATUS[record.status as keyof typeof FUNCTION_STATUS] : null
        },
        staffText(record: Row) {
            const ids = Array.isArray(record.staff_ids) ? record.staff_ids : []
            return ids.map((id: string) => this.dictionaries.staff[id] || id).join(', ') || DASH
        },
        adminLabel(id: string | null | undefined) {
            return adminLabel(this.dictionaries, id)
        },
        open() {
            clearTimeout(this.closeTimer)
            this.opener = document.activeElement as HTMLElement | null
            const dialog = this.$refs.dialog as any
            if (dialog && !dialog.open) dialog.showModal()
            requestAnimationFrame(() => (this.visible = true))
            document.body.style.overflow = 'hidden'
            this.$nextTick(() => (this.$refs.closeButton as HTMLElement | undefined)?.focus())
            this.load()
        },
        async load() {
            if (!this.clientId) return
            this.loading = true
            this.error = false
            this.loaded = false
            const { data, error } = await api.gcb2.getOpz2Client({
                ...(this.request as any),
                projectId: this.projectId,
                clientId: this.clientId,
            })
            this.loading = false
            if (error || !data) {
                this.error = true
                return
            }
            this.card = data.card || {}
            this.names = {
                staff: { ...this.report.staff, ...data.staff },
                clients: { ...this.report.clients, ...data.clients },
                accounts: { ...this.report.accounts, ...data.accounts },
                crmAdmins: { ...this.report.crmAdmins, ...data.crmAdmins },
            }
            this.loaded = true
        },
        close() {
            this.visible = false
            const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280
            this.closeTimer = setTimeout(() => {
                const dialog = this.$refs.dialog as any
                if (dialog?.open) dialog.close()
                document.body.style.overflow = ''
                if (this.opener?.isConnected) this.opener.focus()
                this.$emit('close')
            }, duration)
        },
        onBackdrop(event: MouseEvent) {
            if (event.target !== this.$refs.dialog) return
            const rect = (this.$refs.dialog as HTMLElement).getBoundingClientRect()
            if (
                event.clientX < rect.left ||
                event.clientY < rect.top ||
                event.clientX > rect.right ||
                event.clientY > rect.bottom
            ) {
                this.close()
            }
        },
    },
})
